import {useMutation} from '@apollo/client/react';
import {extractGqlData} from '@coolcolduk/ui-apollo-client';
import {Question} from '@cp/model';
import {FLAG_QUESTION_AS_OUT_OF_SCOPE} from '../sharedQueries';

export function useFlagQuestionAsOutOfScope() {
  const [flagQuestionAsOutOfScopeMutation, {loading, error}] = useMutation<
    {flagQuestionAsOutOfScope: Question},
    {moduleId: string; questionId: string; statusNote?: string}
  >(FLAG_QUESTION_AS_OUT_OF_SCOPE);

  /**
   * Flag a question as out of scope (user reports out of scope)
   * @param moduleId
   * @param questionId
   * @param statusNote
   * @returns Question if successful, otherwise undefined
   */
  const flagQuestionAsOutOfScope = async (moduleId: string, questionId: string, statusNote?: string) => {
    const result = await flagQuestionAsOutOfScopeMutation({
      variables: {moduleId, questionId, statusNote},
      update: (cache, {data}) => {
        if (data?.flagQuestionAsOutOfScope) {
          // Update the question in the cache
          cache.modify({
            id: cache.identify(data.flagQuestionAsOutOfScope),
            fields: {
              status: () => data.flagQuestionAsOutOfScope.status,
              statusNote: () => data.flagQuestionAsOutOfScope.statusNote,
            },
          });

          // Also update any session that contains this question
          // This ensures the currentQuestion in SessionPage gets updated
          cache.modify({
            fields: {
              userSessions: (existingSessions = []) =>
                existingSessions.map((session: any) => {
                  if (session.questions) {
                    const updatedQuestions = session.questions.map((question: any) => {
                      if (question.id === questionId) {
                        return {
                          ...question,
                          status: data.flagQuestionAsOutOfScope.status,
                          statusNote: data.flagQuestionAsOutOfScope.statusNote,
                        };
                      }
                      return question;
                    });
                    return {
                      ...session,
                      questions: updatedQuestions,
                    };
                  }
                  return session;
                }),
            },
          });
        }
      },
    });

    return extractGqlData(result).flagQuestionAsOutOfScope;
  };

  return {
    flagQuestionAsOutOfScope,
    loading,
    error,
  };
}
